import { supabase } from '../lib/supabase'
import type { Deliverable, Project } from '../types/portal'

/**
 * Loads the genuine projects and deliverables from Supabase.
 *
 * The database holds each project's identity (client, name, slug) and its
 * deliverables (key, title, version). Fields the schema does not yet carry —
 * stage, status, visibility, Drive links, shoot details — start blank and are
 * maintained in the session until columns exist for them. Nothing is invented,
 * and no rows are written or modified here.
 */

function warn(what: string, error: { message: string } | null) {
  if (error) console.warn(`[supabase] ${what} failed: ${error.message}`)
}

function storedDeliverable(row: Record<string, unknown>): Deliverable {
  const status = String(row.status ?? '') as Deliverable['status']
  return {
    id: String(row.deliverable_key ?? ''),
    name: String(row.title ?? 'Untitled'),
    type: (String(row.category ?? 'Video') || 'Video') as Deliverable['type'],
    quantity: '',
    status: status || 'Not Started',
    version: String(row.version ?? ''),
    versionDate: '—',
    runtime: '—',
    driveUrl: '',
    audioUrl: '',
    internalNote: '',
    visibleToClient: Boolean(row.visible_to_client),
    notes: [],
    changeRequests: [],
    versions: [],
  }
}

/**
 * One project by its real id — used on the client side once join_project has
 * granted access. Reads the project's identity and its deliverables directly,
 * so nothing depends on being able to list every project.
 */
export async function fetchProjectById(projectId: string): Promise<Project | null> {
  if (!supabase || !projectId) return null

  const { data: row, error } = await supabase
    .from('projects')
    .select('id, client_name, project_name, slug, status')
    .eq('id', projectId)
    .maybeSingle()
  warn('client project load', error)
  if (!row) return null

  const { data: deliverableRows, error: deliverableError } = await supabase
    .from('deliverables')
    .select(
      'project_id, deliverable_key, title, version, category, status, visible_to_client, sort_order',
    )
    .eq('project_id', projectId)
    .order('sort_order', { ascending: true })
  warn('client deliverable load', deliverableError)

  return {
    id: String(row.id),
    slug: String(row.slug ?? ''),
    client: String(row.client_name ?? ''),
    name: String(row.project_name ?? ''),
    type: '',
    description: '',
    status: String(row.status ?? ''),
    nextShootDate: '',
    nextShootTime: '',
    location: '',
    clientContact: '',
    producer: '',
    updated: '',
    deliverables: (deliverableRows ?? []).map((item: Record<string, unknown>) =>
      storedDeliverable(item),
    ),
    shoots: [],
    team: [],
    archived: String(row.status ?? 'active') === 'archived',
  }
}

/** One project by its link slug — used when a client opens a project link. */
export async function fetchProjectBySlug(slug: string): Promise<Project | null> {
  const all = await fetchProjects()
  return all.find((item) => item.slug === slug) ?? null
}

export async function fetchProjects(): Promise<Project[]> {
  if (!supabase) return []

  const { data: projectRows, error } = await supabase
    .from('projects')
    .select('id, client_name, project_name, slug, status')
  warn('project list', error)
  if (!projectRows?.length) return []

  const { data: deliverableRows, error: deliverableError } = await supabase
    .from('deliverables')
    .select(
      'project_id, deliverable_key, title, version, category, status, visible_to_client, sort_order',
    )
    .order('sort_order', { ascending: true })
  warn('deliverable list', deliverableError)

  return projectRows.map((row: Record<string, unknown>) => {
    const id = String(row.id)
    const items = (deliverableRows ?? [])
      .filter((item: Record<string, unknown>) => String(item.project_id) === id)
      .map((item: Record<string, unknown>) => storedDeliverable(item))

    return {
      id,
      slug: String(row.slug ?? ''),
      client: String(row.client_name ?? ''),
      name: String(row.project_name ?? ''),
      type: '',
      description: '',
      status: '',
      nextShootDate: '',
      nextShootTime: '',
      location: '',
      clientContact: '',
      producer: '',
      updated: '',
      deliverables: items,
      shoots: [],
      team: [],
      archived: String(row.status ?? 'active') === 'archived',
    }
  })
}
